import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { EDITOR_RESOLVERS } from '@common/constants';
import { isNodeEmailContainerRow } from '@common/helpers/helpers';
import ColorButtons from '@resolvers/tools/ColorButtons/ColorButtons';
import AlignStyle from '@resolvers/tools/AlignStyle/AlignStyle';

const EmailContainer = ({
  children,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  height,
  size,
  background,
  minWidth,
  align,
}) => {
  const { actions } = useEditor();
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current) {
      connect(drag(ref.current));
    }
  }, []);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('container');
      });
    });
  }, [setCustom, t]);

  const optional = align === 'center' ? { margin: '0 auto' } : {};

  return (
    <table
      ref={ref}
      role='presentation'
      style={{
        tableLayout: 'fixed',
        fontSize: 'inherit',
        paddingLeft: `${paddingLeft || 0}px`,
        paddingRight: `${paddingRight || 0}px`,
        paddingTop: `${paddingTop || 0}px`,
        paddingBottom: `${paddingBottom || 0}px`,
        width: `${size}%`,
        height: `${height}px`,
        backgroundColor: background,
        minWidth: minWidth ? `${minWidth}px` : undefined,
        ...optional,
      }}
    >
      <tbody>{children}</tbody>
    </table>
  );
};

const hAlignOptions = [
  {
    name: 'center',
    iconName: 'center-h',
    iconSize: 'small',
  },
  {
    name: 'left',
    iconName: 'left',
    iconSize: 'small',
  },
];

const EmailTableContainerConfigPanel = () => {
  return (
    <SettingsWrapper>
      <SettingsRow>
        <SizeStyle />
        <SizeStyle propKey='minWidth' max={1500} label='minWidth' />
      </SettingsRow>
      <SettingsRow>
        <SizeStyle propKey='height' max={300} label='height' />
      </SettingsRow>
      <SettingsRow>
        <AlignStyle propKey='align' options={hAlignOptions} />
      </SettingsRow>
      <SettingsRow>
        <ColorButtons hide={{ textColor: true }} />
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <PaddingStyle />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const TableCellDefaultProps = {
  children: null,
  paddingLeft: '10',
  paddingRight: '10',
  paddingTop: '10',
  paddingBottom: '10',
  size: '100',
  height: '60',
  background: 'transparent',
  isDeletable: true,
  minWidth: '0',
  align: 'left',
};

EmailContainer.defaultProps = TableCellDefaultProps;

EmailContainer.propTypes = {
  children: PropTypes.node,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  size: PropTypes.string,
  height: PropTypes.string,
  background: PropTypes.string,
  minWidth: PropTypes.string,
  align: PropTypes.string,
};

EmailContainer.craft = {
  displayName: EDITOR_RESOLVERS.EMAIL.CONTAINER,
  name: EDITOR_RESOLVERS.EMAIL.CONTAINER,
  props: TableCellDefaultProps,
  related: { settings: EmailTableContainerConfigPanel },
  rules: {
    canMoveIn: (incomingNodes) => {
      return incomingNodes.every((node) => {
        return isNodeEmailContainerRow(node);
      });
    },
  },
};

export default EmailContainer;
